import * as Sentry from '@sentry/react'

const CLIENT_SIDE = typeof window !== 'undefined'
const isEnabled = () => CLIENT_SIDE && !!CONFIG?.sentry?.dsn

export const init = () => {
  if (process.env.NODE_ENV === 'test') {
    return
  }

  if (!isEnabled()) {
    console.log('Sentry disabled due to config')
    return
  }

  Sentry.init({
    dsn: CONFIG.sentry.dsn,
    debug: CONFIG.sentry?.debug === true,
    environment: process.env.NODE_ENV,
    tunnel: `${CONFIG.apiURL}/sentry/tunnel`,
    sampleRate: 0.05,
    // Disable Sentry's wrapping of EventTarget.prototype.addEventListener.
    // When combined with New Relic's `nrWrapper` (which also patches
    // addEventListener), the stacked wrappers lose the original `this`
    // binding. Chrome 138+ enforces a brand check on
    // `screen.orientation.addEventListener` (used by react-use-measure),
    // which then throws `TypeError: Illegal invocation`. See SCITE-FRONT-END-D30.
    integrations: defaultIntegrations =>
      defaultIntegrations.map(integration => {
        if (integration.name === 'TryCatch') {
          return new Sentry.Integrations.TryCatch({
            setTimeout: true,
            setInterval: true,
            requestAnimationFrame: true,
            XMLHttpRequest: true,
            eventTarget: false
          })
        }
        return integration
      }),
    ignoreErrors: [
      'ResizeObserver loop', // browser limitation
      '?(gtag/js)', // crawlers dont like gtm
      'Failed to fetch', // network errors from third-party scripts
      '[object HTMLLinkElement]', // browser extensions or third-party scripts throwing DOM elements as errors
      '[object XMLHttpRequest]', // third-party scripts (e.g. UserJot, Churnkey) throwing raw XHR objects as errors
      'Illegal invocation', // residual noise from New Relic's nrWrapper around screen.orientation.addEventListener
      'Request aborted' // bots/crawlers navigating away before XHR completes (axios ECONNABORTED)
    ],
    allowUrls: [/^https:\/\/((cdn(-stage|-sandbox)?|staging|pr-\d+)\.)?scite\.(org|ai)/],
    beforeSend (event, hint) {
      const exceptionValues = event.exception?.values || []
      const firstException = exceptionValues[0]
      const stack = firstException?.stacktrace?.frames || []

      // Check if error comes from UserWay CDN
      const isUserWayError = stack.some(frame => frame.filename && frame.filename.includes('cdn.userway.org'))
      if (isUserWayError) {
        return null // Don't send to Sentry
      }

      // Drop errors with no stack frames where the message looks like a stringified
      // non-Error object (e.g. "[object XMLHttpRequest]", "[object Object]").
      // These originate from third-party scripts throwing raw browser objects rather
      // than proper Error instances; allowUrls cannot filter them because there are
      // no frame filenames to match against.
      const hasNoFrames = stack.length === 0
      const message = firstException?.value || ''
      if (hasNoFrames && /^\[object \w+\]$/.test(message)) {
        return null
      }

      // Suppress handled axios errors for transient server errors (503).
      // These are already caught by forwardPageError/errorWrap in sagas and
      // shown to the user as page-level errors; sending them to Sentry is noise.
      // Also suppress 499 (client closed connection), which occurs when bots/crawlers
      // (e.g. GoogleOther) navigate away before XHR responses complete.
      const isHandled = event.exception?.values?.[0]?.mechanism?.handled === true
      const originalError = hint?.originalException
      if (isHandled && originalError?.isAxiosError && [499, 503].includes(originalError?.response?.status)) {
        return null
      }

      return event
    }
  })
}

export const setUser = user => {
  if (!isEnabled()) return

  Sentry.setUser({
    id: user.slug,
    username: user.name,
    email: user.email
  })
}

export const handlePageChange = location => {
  if (!isEnabled()) return

  Sentry.setContext('page', {
    path: location.pathname
  })
}

export const captureException = (error, context) => {
  if (!isEnabled()) return

  Sentry.captureException(error, context)
}

export const captureMessage = (message, level = 'info') => {
  if (!isEnabled()) return

  Sentry.captureMessage(message, level)
}
